import { useCallback } from 'react';

import { publishBoardSocketPackage } from '@walls/services/boardWebsockets';
import { wallsApi } from '@walls/services/wallsApi';
import {
  useBoardElementRectStore,
  useWallContentStore,
  useWallParticipantsStore,
  useWallStore,
  type WallBoard,
} from '@walls/store';
import { deleteConnectionsByCardId } from '../../../board/connections/connectionDeletion';
import {
  getExistingGroupClustersByGroupId,
  getGroupByGroupCardGroups,
  getGroupByGroupClusterPositions,
  getGroupByGroupClusterWidths,
} from '../../../board/positioning/groupByGroup';
import { isClusterCard } from '../../../board/selectors/boardCardSelectors';

const groupBoundaryMargin = 100;
const groupClusterColor = '#d8f7ff';

export function useGroupCardsByGroup(board: WallBoard) {
  const cards = useWallContentStore((state) => state.cards);
  const measuredRects = useBoardElementRectStore((state) => state.rects);
  const addCard = useWallContentStore((state) => state.addCard);
  const moveCardToCluster = useWallContentStore((state) => state.moveCardToCluster);
  const updateCard = useWallContentStore((state) => state.updateCard);
  const participantGroups = useWallParticipantsStore((state) => state.groups);
  const topicKey = useWallStore((state) => state.topicKey);

  return useCallback(async () => {
    if (!board.isOwner || participantGroups.length === 0) {
      return;
    }

    const viewport = document.getElementById('topic-cards');

    if (!viewport) {
      return;
    }

    const cardGroups = getGroupByGroupCardGroups(cards, participantGroups);
    const existingClustersByGroupId = getExistingGroupClustersByGroupId(cards, cardGroups);
    const clusterWidthsByGroupId = getGroupByGroupClusterWidths(
      cardGroups,
      existingClustersByGroupId,
      measuredRects
    );
    const positions = getGroupByGroupClusterPositions(
      cardGroups,
      {
        height: Math.max(0, viewport.clientHeight - groupBoundaryMargin * 2),
        width: Math.max(0, viewport.clientWidth - groupBoundaryMargin * 2),
        x: groupBoundaryMargin,
        y: groupBoundaryMargin,
      },
      { clusterWidthsByGroupId, measuredRects }
    );
    const positionsByGroupId = new Map(positions.map((position) => [position.groupId, position]));
    const placedOriginalCardIds = new Set<string>();

    for (const group of cardGroups) {
      const position = positionsByGroupId.get(group.group.id);

      if (!position) {
        continue;
      }

      const existingCluster = existingClustersByGroupId.get(group.group.id);
      const cluster =
        existingCluster ??
        (await wallsApi.insertCluster(
          board.id,
          group.group.name,
          groupClusterColor,
          position,
          topicKey
        ));

      if (existingCluster) {
        updateCard(existingCluster.id, { xPos: position.xPos, yPos: position.yPos });
        await wallsApi.updateClusterPosition(
          board.id,
          existingCluster.id,
          { clusterId: existingCluster.id, xPos: position.xPos, yPos: position.yPos },
          topicKey
        );
        publishBoardSocketPackage({
          action: 'updateClusterPosition',
          topic: board.id,
          position: { id: existingCluster.id, xPos: position.xPos, yPos: position.yPos },
        });
      } else {
        addCard(cluster);
        publishBoardSocketPackage({
          action: 'topicClusterAddedToBoard',
          topic: board.id,
          cluster: { ...cluster, boardId: board.id },
        });
      }

      for (const [index, card] of group.cards.entries()) {
        let groupedCard = card;

        if (placedOriginalCardIds.has(card.id)) {
          groupedCard = await wallsApi.duplicateCard(board.id, card.id, topicKey);
          addCard(groupedCard);
          publishBoardSocketPackage({
            action: 'topicCardAddedToBoard',
            topic: board.id,
            card: { ...groupedCard, boardId: board.id },
          });
        }

        if (groupedCard.id === cluster.id) {
          continue;
        }

        if (groupedCard.id === card.id) {
          placedOriginalCardIds.add(card.id);
          await deleteConnectionsByCardId(board.id, card.id, topicKey);
        }

        const zPos = index + 1;
        moveCardToCluster(groupedCard.id, cluster.id, zPos);

        if (isClusterCard(groupedCard)) {
          await wallsApi.addClusterToCluster(
            board.id,
            cluster.id,
            groupedCard.id,
            cluster.collapsed,
            zPos,
            topicKey
          );
          publishBoardSocketPackage({
            action: 'addClusterToCluster',
            topic: board.id,
            updateDetail: {
              sourceClusterId: groupedCard.id,
              targetClusterId: cluster.id,
              zPos,
            },
          });
        } else {
          await wallsApi.addCardToCluster(board.id, cluster.id, groupedCard.id, zPos, topicKey);
          publishBoardSocketPackage({
            action: 'addCardToCluster',
            topic: board.id,
            updateDetail: { cardId: groupedCard.id, clusterId: cluster.id, zPos },
          });
        }
      }
    }
  }, [
    addCard,
    board.id,
    board.isOwner,
    cards,
    measuredRects,
    moveCardToCluster,
    participantGroups,
    topicKey,
    updateCard,
  ]);
}
